import type {
  Airport,
  FareDetailsV2,
  TripDetailsV2,
} from "@b2bportal/air-shopping-api";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { getDurationFromMins } from "@b2bportal/core-utilities";
import { useI18nContext, type DateTimeFormatStyle } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import { AirlineIcon } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import defaultStyles from "./ReviewFlightCard.module.scss";

export interface ReviewFlightCardComponentProps extends PlatformComponentProps {
  tripDetails: TripDetailsV2;
  sliceIndex: number;
  fareDetails: FareDetailsV2;
  airports: { [key: string]: Airport };
  dateFormat?: DateTimeFormatStyle;
  expanded?: boolean;
}
export const ReviewFlightCard = ({
  tripDetails,
  sliceIndex,
  fareDetails,
  airports,
  dateFormat,
  className,
}: ReviewFlightCardComponentProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.ReviewFlightCard;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const tripSlice = tripDetails.slices[sliceIndex];
  const fareSlice = fareDetails.slices[sliceIndex];

  const title = t("flightsPageTitles.detailsOutbound", {
    outboundCity: airports[tripSlice.destinationCode].cityName,
  });

  const time = formatDateTime(
    dayjs(removeTimezone(tripSlice.departureTime)).toDate(),
    dateFormat ?? DateTimeFormatStyle.ShortMonthDayShortWeekday
  );

  const duration = getDurationFromMins(tripSlice.totalDurationMinutes);
  const brandName = fareSlice.fareShelf?.brandName ?? "";
  const primaryCarrier = tripSlice.segmentDetails[0].marketingAirline.code;
  const airlineName = tripSlice.segmentDetails[0].marketingAirline.name;
  const flightStops = tripSlice.segmentDetails.length - 1;
  const allAirlines = new Set(
    tripSlice.segmentDetails.map((segment) => segment.airlineCode)
  );
  const additionalAirlines =
    allAirlines.size > 1 ? allAirlines.size - 1 : undefined;

  const durationString = duration
    .filter((unit) => unit.value)
    .map((unit) => t(`flightDuration.${unit.key}`, { count: unit.value }))
    .join(" ")
    .trim();

  const durationAndStops = `${durationString}, ${
    flightStops === 0
      ? t("stopDetails.nonstop")
      : t("stopDetails.stop", { count: flightStops })
  }`;

  return (
    <div className={clsx(block, className)}>
      <div className={cn("top")}>
        <div className={cn("top", ["destination"])}>{title}</div>
        <div className={cn("top", ["time"])}>{time}</div>
      </div>
      <div className={cn("bottom")}>
        <div className={cn("bottom", ["airline"])}>
          <AirlineIcon
            size="medium"
            airlineCode={primaryCarrier}
            altText={airlineName}
            className={cn("airline-icon")}
          />{" "}
          {airlineName}
          {(additionalAirlines ?? 0) > 0 ? (
            <span className={cn("additional-airlines")}>
              {" "}
              {t("core-flights.vi.countOther", { count: additionalAirlines })}
            </span>
          ) : null}{" "}
          - {brandName}
        </div>
        <div className={cn("bottom", ["duration"])}>{durationAndStops}</div>
      </div>
    </div>
  );
};
